import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Star, Users, Clock, Sparkles, Package } from 'lucide-react';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import { Bundle, Course } from '@/types/api.types';
import { bundleService } from '@/services/bundle.service';
import { courseService } from '@/services/course.service';
import BundleModal from '@/components/modals/BundleModal';
import CourseModal from '@/components/modals/CourseModal';
import CourseBundlesSection from '@/components/sections/CourseBundlesSection';
import { startEnrollment } from '@/utils/enrollment';
import { useNavigate } from 'react-router-dom';
import AIEssentialsBundlesSection from '@/components/sections/AIEssentialsBundleSection';
import UltimatePromptBundleSection from '@/components/sections/UltimatePromptBundleSection';
import { trackBundleCard, trackModal, trackPageView, trackCTA } from '@/services/analytics';
import { useScrollTracking } from '@/hooks/useAnalytics';
import { useSaleInfo } from '@/hooks/useSaleInfo';
import { metaPixel } from '@/services/metaPixel';

// Enterprise-grade interface for bundle courses mapping
interface BundleCoursesMap {
  [bundleId: string]: {
    courses: Course[];
    loading: boolean;
    error: string | null;
    allHidden: boolean;
  };
}

export default function BundlesPage() {
  const [expandedBundle, setExpandedBundle] = useState<string | null>(null);
  const [bundles, setBundles] = useState<Bundle[]>([]);
  const [courses, setCourses] = useState<Course[]>([]);
  const [bundleCourses, setBundleCourses] = useState<BundleCoursesMap>({});
  const [bundleLoading, setBundleLoading] = useState(true);
  const [courseLoading, setCourseLoading] = useState(true);
  const [selectedBundleId, setSelectedBundleId] = useState<string | null>(null);
  const [selectedCourseId, setSelectedCourseId] = useState<string | null>(null);
  const [isBundleModalOpen, setIsBundleModalOpen] = useState(false);
  const [isCourseModalOpen, setIsCourseModalOpen] = useState(false);

  const navigate = useNavigate();

  // Get sale info for displaying sale badges
  const { isSaleActive } = useSaleInfo();

  useScrollTracking('Bundles');

  useEffect(() => {
    // Meta Pixel
    metaPixel.init();
    metaPixel.pageView();
    trackPageView('Bundles');
  }, []);

  useEffect(() => {
    fetchBundles();
    fetchCourses();
  }, []);

  /**
   * Auto-fetch courses for all bundles when bundles are loaded
   */
  useEffect(() => {
    if (bundles.length > 0 && !bundleLoading) {
      bundles.forEach((bundle) => {
        const bundleCourseData = bundleCourses[bundle.id];
        // Only fetch if not already loaded or loading
        if (bundleCourseData && bundleCourseData.courses.length === 0 && !bundleCourseData.loading && !bundleCourseData.error) {
          fetchCoursesForBundle(bundle.id, Array.isArray(bundle.course_ids) ? bundle.course_ids : []);
        }
      });
    }
  }, [bundles, bundleLoading]);

  /**
   * Fetch all bundles with error handling
   */
  const fetchBundles = async () => {
    try {
      setBundleLoading(true);
      const response = await bundleService.getAllBundles({ is_active: true, hidden: false });

      if (!response.success || !response.data) {
        throw new Error(response.error || 'Failed to fetch bundles');
      }

      const fetchedBundles = response.data.bundles || [];
      setBundles(fetchedBundles);

      // Initialize bundle courses map
      const initialMap: BundleCoursesMap = {};
      fetchedBundles.forEach((bundle) => {
        initialMap[bundle.id] = {
          courses: [],
          loading: false,
          error: null,
          allHidden: false,
        };
      });
      setBundleCourses(initialMap);
    } catch (error) {
      console.error('Error fetching bundles:', error);
    } finally {
      setBundleLoading(false);
    }
  };

  /**
   * Fetch all courses with error handling
   */
  const fetchCourses = async () => {
    try {
      setCourseLoading(true);
      const response = await courseService.getAllCourses({ is_active: true, hidden: false });
      if (response.success && response.data) {
        setCourses(response.data.courses || []);
      }
    } catch (error) {
      console.error('Error fetching courses:', error);
    } finally {
      setCourseLoading(false);
    }
  };

  /**
   * Fetch courses for a specific bundle with individual API calls
   */
  const fetchCoursesForBundle = async (bundleId: string, courseIds: string[]) => {
    const validCourseIds = Array.isArray(courseIds) ? courseIds : [];

    if (validCourseIds.length === 0) {
      setBundleCourses(prev => ({
        ...prev,
        [bundleId]: { courses: [], loading: false, error: 'No courses in this bundle', allHidden: false }
      }));
      return;
    }

    setBundleCourses(prev => ({
      ...prev,
      [bundleId]: { ...prev[bundleId], loading: true, error: null }
    }));

    try {
      const coursePromises = validCourseIds.map(courseId =>
        courseService.getCourseById(courseId)
          .catch(err => {
            console.error(`Failed to fetch course ${courseId}:`, err);
            return null;
          })
      );

      const courseResponses = await Promise.all(coursePromises);

      const allFetchedCourses = courseResponses
        .filter(response => response && response.success && response.data)
        .map(response => response!.data as Course);

      const visibleCourses = allFetchedCourses.filter(course => !course.hidden);
      const allHidden = allFetchedCourses.length > 0 && visibleCourses.length === 0;

      setBundleCourses(prev => ({
        ...prev,
        [bundleId]: {
          courses: visibleCourses,
          loading: false,
          error: visibleCourses.length === 0 && !allHidden ? 'Failed to load courses' : null,
          allHidden
        }
      }));
    } catch (error) {
      console.error(`Error fetching courses for bundle ${bundleId}:`, error);
      setBundleCourses(prev => ({
        ...prev,
        [bundleId]: {
          courses: [],
          loading: false,
          error: 'Failed to load courses. Please try again.',
          allHidden: false
        }
      }));
    }
  };

  /**
   * Toggle bundle expansion and fetch courses if not already loaded
   */
  const toggleBundle = (bundleId: string, bundle: Bundle) => {
    const isExpanding = expandedBundle !== bundleId;
    setExpandedBundle(isExpanding ? bundleId : null);

    if (isExpanding && bundleCourses[bundleId]?.courses.length === 0 && !bundleCourses[bundleId]?.loading) {
      fetchCoursesForBundle(bundleId, Array.isArray(bundle.course_ids) ? bundle.course_ids : []);
    }
  };

  /**
   * Retry fetching courses for a bundle
   */
  const retryFetchCourses = (bundleId: string, courseIds: string[]) => {
    fetchCoursesForBundle(bundleId, courseIds);
  };

  const handleBundleClick = (bundleId: string) => {
    setSelectedBundleId(bundleId);
    setIsBundleModalOpen(true);
  };

  const handleCourseClick = (courseId: string) => {
    setSelectedCourseId(courseId);
    setIsCourseModalOpen(true);
  };

  const handleCloseBundleModal = () => {
    setIsBundleModalOpen(false);
    setSelectedBundleId(null);
  };

  const handleCloseCourseModal = () => {
    setIsCourseModalOpen(false);
    setSelectedCourseId(null);
  };

  const handleEnrollBundle = (bundleId: string) => {
    startEnrollment(bundleId, 'bundle', navigate)
    handleCloseBundleModal();
  };

  const handleEnrollCourse = (courseId: string) => {
    startEnrollment(courseId, 'course', navigate)
    handleCloseCourseModal();
  };

  return (
    <>
      <Helmet>
        <title>Course Bundles - Save Up To 67% | AI Mastery Hub</title>
        <meta name="description" content="Explore our comprehensive AI course bundles and save up to 67%. Get lifetime access to multiple courses at incredible discounted prices." />

        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://ai-mastery-hub.com/bundles" />
        <meta property="og:title" content="Course Bundles - Save Up To 67% | AI Mastery Hub" />
        <meta property="og:description" content="Bundle & save up to 67% on comprehensive AI courses. Get lifetime access at incredible prices." />
        <meta property="og:image" content="https://ai-mastery-hub.com/bundles-og-image.jpg" />

        {/* Twitter */}
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content="https://ai-mastery-hub.com/bundles" />
        <meta property="twitter:title" content="Course Bundles - Save Up To 67%" />
        <meta property="twitter:description" content="Bundle & save up to 67% on AI courses." />
        <meta property="twitter:image" content="https://ai-mastery-hub.com/bundles-og-image.jpg" />

        {/* Additional SEO */}
        <meta name="keywords" content="AI course bundles, course packages, bundle deals, online education bundles, AI training packages, discounted courses" />
        <link rel="canonical" href="https://ai-mastery-hub.com/bundles" />

        {/* Schema.org for Bundle Catalog */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "ItemList",
            "name": "AI Mastery Hub Course Bundles",
            "description": "Comprehensive AI course bundles",
            "url": "https://ai-mastery-hub.com/bundles"
          })}
        </script>
      </Helmet>

      <div className="min-h-screen bg-black text-white">
        {/* Navigation */}
        <Navigation/>

        {/* Hero Section */}
        <section className="relative pt-32 pb-12 px-6 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-purple-900/20 via-black to-blue-900/20"></div>
          <div className="absolute inset-0" style={{
            backgroundImage: 'radial-gradient(circle at 50% 50%, rgba(139, 92, 246, 0.1) 0%, transparent 50%)',
          }}></div>

          <div className="max-w-7xl mx-auto relative z-10 text-center">
            <div className="inline-block px-4 py-2 bg-purple-600/20 border border-purple-500/30 rounded-full text-sm mb-6">
              <span className="text-purple-400">💎 Bundle & Save Up To 67%</span>
            </div>
            <h1 className="text-5xl md:text-7xl font-bold leading-tight mb-6">
              <span className="block">Get More for Less</span>
              <span className="block bg-gradient-to-r from-purple-400 via-blue-400 to-cyan-400 bg-clip-text text-transparent">
                With Course Bundles
              </span>
            </h1>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto mb-8">
              Choose from our carefully curated course bundles and save hundreds of dollars.
              Perfect packages for every learning journey - from beginner to expert.
            </p>
            <div className="flex flex-wrap justify-center gap-6 text-sm">
              <div className="flex items-center space-x-2">
                <Users className="w-5 h-5 text-purple-400" />
                <span className="text-gray-400">Growing Community</span>
              </div>
              <div className="flex items-center space-x-2">
                <Star className="w-5 h-5 text-yellow-400 fill-yellow-400" />
                <span className="text-gray-400">Robust Curriculum</span>
              </div>
              <div className="flex items-center space-x-2">
                <Clock className="w-5 h-5 text-blue-400" />
                <span className="text-gray-400">Lifetime Access</span>
              </div>
              <div className="flex items-center space-x-2">
                <Package className="w-5 h-5 text-green-400" />
                <span className="text-gray-400">Best Value Packages</span>
              </div>
            </div>
          </div>
        </section>

        {/* Course Bundles Section */}
        <AIEssentialsBundlesSection/>

        {/* Digital Download Bundle Section */}
        <UltimatePromptBundleSection/>

        {/* Why Bundle Section */}
        <section className="py-20 px-6 bg-gradient-to-b from-transparent to-gray-900/50">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold mb-4">Why Choose Bundles?</h2>
              <p className="text-xl text-gray-400 max-w-2xl mx-auto">
                Get the complete learning experience and save significantly
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              {/* Benefit 1 */}
              <div className="bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-xl rounded-2xl p-8 border border-white/20 hover:border-purple-500/50 transition-all">
                <div className="w-16 h-16 bg-gradient-to-br from-purple-600 to-violet-600 rounded-2xl flex items-center justify-center mb-6 shadow-2xl shadow-purple-500/50">
                  <Sparkles className="w-8 h-8" strokeWidth={2.5} />
                </div>
                <h3 className="text-2xl font-bold mb-4">Massive Savings</h3>
                <p className="text-gray-400 leading-relaxed">
                  Save up to 67% compared to buying courses individually. Get more value for your investment with our carefully curated bundles.
                </p>
              </div>

              {/* Benefit 2 */}
              <div className="bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-xl rounded-2xl p-8 border border-white/20 hover:border-blue-500/50 transition-all">
                <div className="w-16 h-16 bg-gradient-to-br from-blue-600 to-cyan-600 rounded-2xl flex items-center justify-center mb-6 shadow-2xl shadow-blue-500/50">
                  <Package className="w-8 h-8" strokeWidth={2.5} />
                </div>
                <h3 className="text-2xl font-bold mb-4">Complete Learning Paths</h3>
                <p className="text-gray-400 leading-relaxed">
                  Each bundle is designed to take you from beginner to advanced. Follow a structured path to mastery.
                </p>
              </div>

              {/* Benefit 3 */}
              <div className="bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-xl rounded-2xl p-8 border border-white/20 hover:border-green-500/50 transition-all">
                <div className="w-16 h-16 bg-gradient-to-br from-green-600 to-emerald-600 rounded-2xl flex items-center justify-center mb-6 shadow-2xl shadow-green-500/50">
                  <Clock className="w-8 h-8" strokeWidth={2.5} />
                </div>
                <h3 className="text-2xl font-bold mb-4">Lifetime Access</h3>
                <p className="text-gray-400 leading-relaxed">
                  Get lifetime access to all courses in the bundle, including all future updates and additions.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 px-6 bg-black">
          <div className="max-w-5xl mx-auto">
            <div className="relative bg-gradient-to-r from-purple-600 to-blue-600 rounded-3xl p-12 text-center overflow-hidden">
              <div className="absolute inset-0 bg-black/20"></div>
              <div className="relative z-10">
                <h2 className="text-4xl md:text-5xl font-bold mb-6">
                  Ready to Start Learning?
                </h2>
                <p className="text-xl mb-8 text-white/90">
                  Join learners who are already transforming their careers with AI
                </p>
                <button
                  onClick={() => { trackCTA.click('bundles-get-started', 'Get Started Today', 'cta-section', '/sign-up'); navigate('/sign-up'); }}
                  className="bg-white text-black px-10 py-4 rounded-full font-bold text-lg hover:bg-gray-100 transition-all hover:scale-105 hover:shadow-2xl"
                >
                  Get Started Today
                </button>
                <p className="text-sm mt-4 text-white/70">Choose your perfect bundle and start learning.</p>
              </div>
            </div>
          </div>
        </section>

        {/* Footer */}
        <Footer />
      </div>

      {/* Modals */}
      <BundleModal
        bundleId={selectedBundleId}
        isOpen={isBundleModalOpen}
        onClose={handleCloseBundleModal}
        onEnroll={handleEnrollBundle}
      />

      <CourseModal
        courseId={selectedCourseId}
        isOpen={isCourseModalOpen}
        onClose={handleCloseCourseModal}
        onEnroll={handleEnrollCourse}
      />
    </>
  );
}
